<script setup lang="ts">
import { computed, reactive, watch, onMounted, ref, watchEffect } from "vue";

const props = defineProps<{
    modelValue: string;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: string): void;
    (e: "valid-change", value: boolean): void;
}>();

const state = reactive({
    showPassword: false,
    passwordValidations: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
    },
});

const strength = ref(0);

function validatePassword(password: string) {
    state.passwordValidations.minLength = password.length >= 8;
    state.passwordValidations.hasUppercase = /[A-Z]/.test(password);
    state.passwordValidations.hasLowercase = /[a-z]/.test(password);
    state.passwordValidations.hasNumber = /\d/.test(password);
    state.passwordValidations.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
}

const isPasswordValid = computed(() => {
    return Object.values(state.passwordValidations).every((validation) => validation);
});

watchEffect(() => {
    strength.value = 0;
    if (props.modelValue.length > 8) strength.value++;
    if (props.modelValue.length > 12 || /[\W_]/.test(props.modelValue)) strength.value++;
    if (/\d/.test(props.modelValue) && /[a-z]/i.test(props.modelValue)) strength.value++;
    if (/[A-Z]/.test(props.modelValue)) strength.value++;
});

const strengthText = computed(() => {
    switch (strength.value) {
        case 1:
            return "Weak";
        case 2:
            return "Medium";
        case 3:
            return "Good";
        case 4:
            return "Great";
        default:
            return "Very Weak";
    }
});

const blockClass = (n: number) => {
    if (n <= strength.value) {
        switch (strength.value) {
            case 1:
                return "bg-red";
            case 2:
                return "bg-yellow-500";
            case 3:
                return "bg-green-new";
            case 4:
                return "bg-green";
        }
    } else {
        return "bg-blue-light";
    }
};

watch(
    () => props.modelValue,
    (newPassword) => {
        validatePassword(newPassword);
    },
    { immediate: true },
);

watch(
    () => strength.value,
    (newStrength) => {
        emit("valid-change", newStrength >= 2);
    },
    { immediate: true },
);

function onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    emit("update:modelValue", value);
}

onMounted(() => {
    validatePassword(props.modelValue);
    emit("valid-change", isPasswordValid.value);
});
</script>

<template>
    <div class="flex flex-col gap-2">
        <div class="relative">
            <input
                :type="state.showPassword ? 'text' : 'password'"
                :value="modelValue"
                @input="onInput"
                placeholder="PASSWORD"
                class="input w-full pr-12"
            />
            <button
                type="button"
                @click="state.showPassword = !state.showPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 flex items-center justify-center h-5 w-5 leading-none"
            >
                <span v-if="state.showPassword" class="material-symbols-outlined text-[20px]"
                    >visibility_off</span
                >
                <span v-else class="material-symbols-outlined text-[20px]">visibility</span>
            </button>
        </div>
        <div>
            <div>
                <div class="flex mt-2 gap-2">
                    <div
                        v-for="n in 4"
                        :key="n"
                        :class="blockClass(n)"
                        class="w-1/4 h-2 rounded"
                    ></div>
                </div>
            </div>
            <div class="mt-2 flex justify-end">
                <p class="font-mono text-xs text-gray-dark">
                    {{ strengthText }}
                </p>
            </div>
        </div>
    </div>
</template>
